import React, { useState } from 'react';
import { usePlans } from 'hooks/plan';
import { Plan, Interval } from 'models/Plan';
import useLifetimePromo from 'hooks/useLifetimePromo';
import useCountdownTimer from 'hooks/useCountdownTimer';
import getLifetimeCheckoutLink from 'services/Subscriprion/getLifetimeCheckoutLink';
import { trackLifetimeCheckoutClick } from 'utils/tracking';
import { FaArrowRight, FaLock, FaTag } from 'react-icons/fa';
import './PanicBanner.scss';
import { useUser } from 'hooks/user';
import { useAppContext } from 'hooks/app';
import { Modals } from 'constants/modals';
import { useCaptureAbandonedCheckout } from 'hooks/abandonedCheckout';

const PanicBanner: React.FC = () => {
  const { plans } = usePlans();
  const { discountPct, slotsLeft, discountedPrice, isLoading: promoLoading } = useLifetimePromo();
  const { hours, minutes, seconds } = useCountdownTimer();
  const [loading, setLoading] = useState(false);
  const { user } = useUser();
  const { openModal } = useAppContext();
  const { capture } = useCaptureAbandonedCheckout();

  const lifetimePlan = plans?.find((p: Plan) => p.interval === Interval.LIFETIME);

  if (!lifetimePlan) return null;

  const originalPrice = Number(lifetimePlan.price);
  const salePrice = discountedPrice(originalPrice);
  const savedAmount = originalPrice - salePrice;

  const handleCheckout = async () => {
    trackLifetimeCheckoutClick(lifetimePlan.name, salePrice);
    if (!user?.id) {
      openModal?.(Modals.LEAD_CAPTURE, { planType: "4", planName: lifetimePlan.name });
      return;
    }
    capture({ email: user.email, source: 'panic-banner', plan: lifetimePlan.name });
    setLoading(true);
    try {
      const url = await getLifetimeCheckoutLink();
      window.location.href = url;
    } catch {
      setLoading(false);
    }
  };

  return (
    <section className="panic-banner">
      <div className="pb-container">

        {/* Top label */}
        <div className="pb-label">
          <FaTag className="pb-label-icon" />
          <span>{discountPct > 0 ? `Flash Sale — ${discountPct}% Off, Today Only` : 'Limited Time Offer'}</span>
        </div>

        {/* Headline */}
        <h2 className="pb-headline">
          {discountPct > 0 ? "Don't Miss This Deal — It Expires Soon!" : 'This Offer Closes Soon — Act Now!'}
        </h2>
        <p className="pb-sub">
          {discountPct > 0
            ? 'This exclusive discount is only available for a limited window. Once the timer hits zero, the price goes back up.'
            : 'Secure your lifetime access before this window closes. The timer resets every 2 hours.'}
        </p>
        <div className="pb-offer-details">
            {/* Timer */}
            <div className="pb-timer">
              <div className="pb-timer-block">
                <span className="pb-timer-digit">{hours}</span>
                <span className="pb-timer-label">Hours</span>
              </div>
              <span className="pb-timer-colon">:</span>
              <div className="pb-timer-block">
                <span className="pb-timer-digit">{minutes}</span>
                <span className="pb-timer-label">Minutes</span>
              </div>
              <span className="pb-timer-colon">:</span>
              <div className="pb-timer-block">
                <span className="pb-timer-digit">{seconds}</span>
                <span className="pb-timer-label">Seconds</span>
              </div>
            </div>

            {/* Pricing — only when a discount is active */}
            {discountPct > 0 && !promoLoading && (
              <div className="pb-pricing">
                <span className="pb-original">Rs. {originalPrice.toLocaleString()}</span>
                <span className="pb-arrow">→</span>
                <span className="pb-sale">Rs. {salePrice.toLocaleString()}</span>
                <span className="pb-save-badge">Save Rs. {savedAmount.toLocaleString()}</span>
              </div>
            )}
        </div>
        

        {/* Slots left */}
        {slotsLeft !== null && (
          <p className="pb-slots">Only <strong>{slotsLeft}</strong> slots remaining at this price</p>
        )}

        {/* CTA */}
        <button
          className="pb-cta"
          onClick={handleCheckout}
          disabled={loading}
        >
          {loading ? 'Redirecting to checkout...' : (
            <>
              {discountPct > 0 ? `Claim ${discountPct}% Discount Now` : 'Claim Lifetime Access Now'}
              <FaArrowRight className="pb-cta-icon" />
            </>
          )}
        </button>

        {/* Trust line */}
        <p className="pb-trust">
          <FaLock className="pb-trust-icon" />
          Secure checkout via Stripe — discount applied automatically
        </p>

      </div>
    </section>
  );
};

export default PanicBanner;
